import { Injectable } from '@angular/core';
import { AppPaymentResponse } from '@models/api-calls/application/application-response.model';
import { ClientResponse } from '@models/api-calls/clients/client-response.model';
import { ClientAddress } from '@models/api-calls/clients/client.model';
import { AppPayment, Payer, PayerContact } from '@models/api-calls/payment/payment.model';
import { DateUtil } from '@utils/date.util';
import { stringToEnum } from '@utils/enum.util';
import { PaymentDateEnum, PaymentFrequencyEnum, PaymentFrequencyValueEnum, States } from 'app/enums/form/form.enum';

@Injectable({
    providedIn: 'root',
})
export class PaymentMappingService {
    mapAppPaymentResponseToAppPayment(response: AppPaymentResponse): AppPayment {
        return {
            id: response.id,
            payment_frequency: this.mapPaymentFrequency(response.payment_frequency),
            payment_date_options: stringToEnum(PaymentDateEnum, response.payment_date_options),
            payment_ssd: response.payment_ssd === 1,
            initial_payment_at: response.initial_payment_at ? DateUtil.fromServer(response.initial_payment_at) : null,
            initial_payment_day: response.initial_payment_day,
            initial_payment_day_details: response.initial_payment_day_details,
            draw_at: response.draw_at ? DateUtil.fromServer(response.draw_at) : null,
            draw_recurring_day: response.draw_recurring_day,
            draw_recurring_day_details: response.draw_recurring_day_details,
            primary_payer_id: response.primary_payer_id,
            secondary_payer_id: response.secondary_payer_id,
            payer_contact_id: response.payer_contact_id,
            primary_payer: response.primary_payer ? this.mapClientResponseToPayer(response.primary_payer) : null,
            secondary_payer: response.secondary_payer ? this.mapClientResponseToPayer(response.secondary_payer) : null,
            payer_contact: response.payer_contact ? this.mapClientResponseToPayerContact(response.payer_contact) : null,
            // Payment method and verification fields
            primary_payment_method: response.primary_payment_method,
            primary_payment_status: response.primary_payment_status,
            primary_payment_verification: response.primary_payment_verification,
            secondary_payment_method: response.secondary_payment_method,
            secondary_payment_status: response.secondary_payment_status,
            secondary_payment_verification: response.secondary_payment_verification,
            primary_payment_verification_data: response.primary_payment_verification_data ?? null,
            secondary_payment_verification_data: response.secondary_payment_verification_data ?? null,
            // Payment disclosures
            has_payment_disclosures: response.has_payment_disclosures,
            secondary_payment_selected: response.secondary_payment_selected,
        };
    }

    private mapPaymentFrequency(frequency: number): PaymentFrequencyEnum | null {
        if (frequency === PaymentFrequencyValueEnum.MONTHLY) {
            return PaymentFrequencyEnum.MONTHLY;
        } else if (frequency === PaymentFrequencyValueEnum.ANNUAL) {
            return PaymentFrequencyEnum.ANNUAL;
        }
        return null;
    }

    private mapClientResponseToPayer(response: ClientResponse): Payer {
        return {
            first_name: response.first_name,
            middle_name: response.middle_name,
            last_name: response.last_name,
            relationship: response.relationship,
            address: this.mapClientResponseToAddress(response),
        };
    }

    private mapClientResponseToPayerContact(response: ClientResponse): PayerContact {
        return {
            id: response.id ?? null,
            first_name: response.first_name,
            middle_name: response.middle_name,
            last_name: response.last_name,
            relationship: response.relationship,
            address: this.mapClientResponseToAddress(response),
        };
    }

    private mapClientResponseToAddress(response: ClientResponse): ClientAddress {
        return {
            address: response.address,
            address2: response.address2,
            city: response.city,
            state_code: stringToEnum(States, response.state_code),
            zip_code: response.zip_code,
            address_dpv_code: response.address_dpv_code,
            address_dpv_data: response.address_dpv_data,
            country_code: response.country_code,
        };
    }
}
